import React, { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import { LbSelect } from '@infoods/lifebase-ui';

import classes from './ReportForm.module.scss';
import {
  LbTabs,
  LbToggleTabsList,
  TabOption,
} from '../../../../../shared/components/tabs';
import {
  ProgressReportFormValues,
  REPORT_MODE,
  ReportMode,
} from '../ProgressReportModal.settings';
import {
  ComboBox,
  OptionWithDescription,
} from '../../../../../shared/components/form-components';
import { useFilterScansByInbodyType } from '../../../../hooks/useFilterScansByInbodyType/useFilterScansByInbodyType';

import { ScanMetadata } from '../../../../interfaces/scanMetadata';

import { useScanRangeOptions } from '../../../../hooks/useScanRangeOptions/useScanRangeOptions';
import { useScanDateOptions } from '../../../../hooks/useScanDateOptions/useScanDateOptions';
import { useInbodyTypeOptions } from '../../../../hooks/useInbodyTypeOptions/useInbodyTypeOptions';
import { getDefaultFormValue } from '../utils/getDefaultFormValue';
import { ProgressReportParams } from '../../../../services/scanService/types';
import { getProgressReportParams } from '../utils/getProgressReportParams';

type ReportFormProps = {
  formId: string;
  scansMetadata: ScanMetadata[];
  onSubmit: (params: ProgressReportParams | null) => void;
};

const ReportForm = ({ formId, scansMetadata, onSubmit }: ReportFormProps) => {
  const [mode, setMode] = useState<ReportMode>(REPORT_MODE.SINGLE);
  const { control, watch, reset, handleSubmit } = useForm<ProgressReportFormValues>({
    defaultValues: getDefaultFormValue(scansMetadata),
  });

  const { inbodyType: selectedInbodyType, startScanId, endScanId } = watch();

  const inbodyTypeOptions = useInbodyTypeOptions(scansMetadata);

  const machineScans = useFilterScansByInbodyType(scansMetadata, selectedInbodyType);
  const isTrendDisabled = machineScans.length < 2;

  const scanOptions = useScanDateOptions(machineScans);
  const { startScanOptions, endScanOptions } = useScanRangeOptions({
    scansMetadata: machineScans,
    startScanId,
    endScanId,
  });

  useUpdateEffect(() => {
    if (isTrendDisabled) {
      setMode(REPORT_MODE.SINGLE);
    }

    const defaultValues = getDefaultFormValue(machineScans);
    reset(defaultValues);
  }, [selectedInbodyType]);

  const tabsConfig: TabOption[] = [
    { label: 'Single Scan', value: REPORT_MODE.SINGLE },
    {
      label: 'Trend',
      value: REPORT_MODE.TREND,
      disabled: isTrendDisabled,
      tooltipText: isTrendDisabled
        ? 'At least two scans of this InBody machine are required'
        : undefined,
    },
  ];

  const handleFormSubmit = handleSubmit((data) =>
    onSubmit(getProgressReportParams(mode, data)),
  );

  const generalInputProps = {
    control,
    variant: 'outlined',
    size: 'small',
  } as const;

  return (
    <form className={classes.form} id={formId} onSubmit={handleFormSubmit} noValidate>
      <LbTabs value={mode} onValueChange={(value: ReportMode) => setMode(value)}>
        <LbToggleTabsList tabsConfig={tabsConfig} size="small" fullWidth />
      </LbTabs>

      <LbSelect
        {...generalInputProps}
        name="inbodyType"
        label="InBody Machine"
        options={inbodyTypeOptions}
        rules={{ required: 'InBody Machine is required' }}
      />

      {mode === REPORT_MODE.SINGLE ? (
        <ComboBox
          {...generalInputProps}
          name="scanId"
          label="Scan"
          options={scanOptions}
          rules={{ required: 'Scan is required' }}
          renderOptionLabel={(option) => (
            <OptionWithDescription
              label={option.label}
              description={option.description}
            />
          )}
        />
      ) : (
        <div className={classes.range}>
          <ComboBox
            {...generalInputProps}
            name="startScanId"
            label="From"
            options={startScanOptions}
            rules={{ required: 'From is required' }}
            renderOptionLabel={(option) => (
              <OptionWithDescription
                label={option.label}
                description={option.description}
              />
            )}
          />
          <EastRoundedIcon fontSize="small" />
          <ComboBox
            {...generalInputProps}
            name="endScanId"
            label="To"
            options={endScanOptions}
            rules={{ required: 'To is required' }}
            renderOptionLabel={(option) => (
              <OptionWithDescription
                label={option.label}
                description={option.description}
              />
            )}
          />
        </div>
      )}
    </form>
  );
};

export { ReportForm };
